import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Truck, CheckCircle2, Loader2, Sparkles, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { cart, sessionId, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    shipping_street: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    payment_method: 'credit_card',
    card_number: '',
    card_expiry: '',
    card_cvv: ''
  });
  const [sameAsShipping, setSameAsShipping] = useState(true);

  const subtotal = cart.total || 0;
  const tax = subtotal * 0.08;
  const shipping = subtotal > 50 ? 0 : 9.99;
  const total = subtotal + tax + shipping;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        shipping_address: {
          street: formData.shipping_street,
          city: formData.shipping_city,
          state: formData.shipping_state,
          zip: formData.shipping_zip,
          country: 'United States'
        },
        billing_address: sameAsShipping ? {
          street: formData.shipping_street,
          city: formData.shipping_city,
          state: formData.shipping_state,
          zip: formData.shipping_zip,
          country: 'United States'
        } : {
          street: formData.billing_street,
          city: formData.billing_city,
          state: formData.billing_state,
          zip: formData.billing_zip,
          country: 'United States'
        },
        payment_method: formData.payment_method,
        session_id: sessionId
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      toast({
        title: "Order placed successfully!",
        description: `Order #${response.data.order_number}`,
      });

      navigate(`/order/${response.data._id}/success`);
    } catch (error) {
      console.error('Error placing order:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to place order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="checkout-page">
      {/* Page Header */}
      <div className="bg-stone-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
              repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
            `
          }}
        ></div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="text-amber-400 text-sm tracking-[0.2em] uppercase">Secure Checkout</span>
          </div>
          <h1 className="text-4xl font-black text-white">Complete Your Order</h1>
        </div>
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 30px, 
              #dc2626 30px, #dc2626 60px, 
              #f59e0b 60px, #f59e0b 90px)`
          }}
        ></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <div className="bg-white p-6 shadow-lg border-l-4 border-amber-600" data-testid="shipping-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
                    <Truck className="h-5 w-5 text-amber-700" />
                  </div>
                  <h2 className="text-2xl font-black text-stone-900">Shipping Address</h2>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="shipping_street" className="text-stone-700">Street Address</Label>
                    <Input
                      id="shipping_street"
                      name="shipping_street"
                      value={formData.shipping_street}
                      onChange={handleInputChange}
                      required
                      className="rounded-none border-stone-300 focus:border-amber-500"
                      data-testid="shipping-street"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_city" className="text-stone-700">City</Label>
                    <Input
                      id="shipping_city"
                      name="shipping_city"
                      value={formData.shipping_city}
                      onChange={handleInputChange}
                      required
                      className="rounded-none border-stone-300 focus:border-amber-500"
                      data-testid="shipping-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_state" className="text-stone-700">State</Label>
                    <Input
                      id="shipping_state"
                      name="shipping_state"
                      value={formData.shipping_state}
                      onChange={handleInputChange}
                      required
                      className="rounded-none border-stone-300 focus:border-amber-500"
                      data-testid="shipping-state"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_zip" className="text-stone-700">ZIP Code</Label>
                    <Input
                      id="shipping_zip"
                      name="shipping_zip"
                      value={formData.shipping_zip}
                      onChange={handleInputChange}
                      required
                      className="rounded-none border-stone-300 focus:border-amber-500"
                      data-testid="shipping-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="bg-white p-6 shadow-lg border-l-4 border-red-600" data-testid="billing-form">
                <h2 className="text-2xl font-black mb-4 text-stone-900">Billing Address</h2>
                
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameAsShipping}
                      onChange={(e) => setSameAsShipping(e.target.checked)}
                      className="rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                      data-testid="same-as-shipping"
                    />
                    <span className="text-stone-700">Same as shipping address</span>
                  </label>
                </div>

                {!sameAsShipping && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="billing_street" className="text-stone-700">Street Address</Label>
                      <Input
                        id="billing_street"
                        name="billing_street"
                        value={formData.billing_street}
                        onChange={handleInputChange}
                        required={!sameAsShipping}
                        className="rounded-none border-stone-300 focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_city" className="text-stone-700">City</Label>
                      <Input
                        id="billing_city"
                        name="billing_city"
                        value={formData.billing_city}
                        onChange={handleInputChange}
                        required={!sameAsShipping}
                        className="rounded-none border-stone-300 focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_state" className="text-stone-700">State</Label>
                      <Input
                        id="billing_state"
                        name="billing_state"
                        value={formData.billing_state}
                        onChange={handleInputChange}
                        required={!sameAsShipping}
                        className="rounded-none border-stone-300 focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_zip" className="text-stone-700">ZIP Code</Label>
                      <Input
                        id="billing_zip"
                        name="billing_zip"
                        value={formData.billing_zip}
                        onChange={handleInputChange}
                        required={!sameAsShipping}
                        className="rounded-none border-stone-300 focus:border-amber-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="bg-white p-6 shadow-lg border-l-4 border-orange-600" data-testid="payment-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-orange-700" />
                  </div>
                  <h2 className="text-2xl font-black text-stone-900">Payment Information</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="card_number" className="text-stone-700">Card Number</Label>
                    <Input
                      id="card_number"
                      name="card_number"
                      placeholder="1234 5678 9012 3456"
                      value={formData.card_number}
                      onChange={handleInputChange}
                      required
                      className="rounded-none border-stone-300 focus:border-amber-500"
                      data-testid="card-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="card_expiry" className="text-stone-700">Expiry Date</Label>
                      <Input
                        id="card_expiry"
                        name="card_expiry"
                        placeholder="MM/YY"
                        value={formData.card_expiry}
                        onChange={handleInputChange}
                        required
                        className="rounded-none border-stone-300 focus:border-amber-500"
                        data-testid="card-expiry"
                      />
                    </div>
                    <div>
                      <Label htmlFor="card_cvv" className="text-stone-700">CVV</Label>
                      <Input
                        id="card_cvv"
                        name="card_cvv"
                        placeholder="123"
                        value={formData.card_cvv}
                        onChange={handleInputChange}
                        required
                        className="rounded-none border-stone-300 focus:border-amber-500"
                        data-testid="card-cvv"
                      />
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                    <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Demo Mode:</strong> This is a mock payment. Your order will be processed without actual charges.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 shadow-lg sticky top-8 border-t-4 border-amber-600" data-testid="checkout-summary">
                <h2 className="text-2xl font-black mb-6 text-stone-900">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-stone-600">{item.name} × {item.quantity}</span>
                      <span className="font-semibold text-stone-900">${item.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-200 pt-4 space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Subtotal</span>
                    <span className="font-semibold text-stone-900">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Tax</span>
                    <span className="font-semibold text-stone-900">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Shipping</span>
                    <span className="font-semibold text-stone-900">
                      {shipping === 0 ? <span className="text-green-600">FREE</span> : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="border-t border-stone-200 pt-3">
                    <div className="flex justify-between text-xl font-black">
                      <span className="text-stone-900">Total</span>
                      <span className="text-stone-900">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6 rounded-none"
                  size="lg"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Place Order
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
